import React from 'react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/admin/components/ui/dialog';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  createAdminInviteCode,
  disableAdminInviteCode,
  fetchAdminInviteCodes,
  type AdminInviteCode,
} from '@/admin/lib/api';

type BenefitType = 'none' | 'subscription_months' | 'one_time_plan';

const statusVariant = (
  status: string
): 'default' | 'secondary' | 'destructive' => {
  switch (status.toLowerCase()) {
    case 'disabled':
      return 'destructive';
    default:
      return 'default';
  }
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isBenefitType = (value: string): value is BenefitType =>
  value === 'none' ||
  value === 'subscription_months' ||
  value === 'one_time_plan';

const benefitLabel = (code: AdminInviteCode) => {
  const benefit = code.benefit;
  if (!benefit) return '—';

  if (benefit.type === 'subscription_months') {
    const months = benefit.duration_months ?? 0;
    return `${benefit.plan_code} · ${months} mo gift`;
  }

  return `${benefit.plan_code} · one-time`;
};

type InviteFormValues = {
  code: string;
  ownerUserId: string;
  maxUses: string;
  benefitType: BenefitType;
  benefitPlanCode: string;
  durationMonths: string;
};

export const InviteCodesPage: React.FC = () => {
  const [codes, setCodes] = React.useState<AdminInviteCode[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [disablingId, setDisablingId] = React.useState<string | null>(null);

  const [formValues, setFormValues] = React.useState<InviteFormValues>({
    code: '',
    ownerUserId: '',
    maxUses: '',
    benefitType: 'none',
    benefitPlanCode: '',
    durationMonths: '3',
  });

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const codesResponse = await fetchAdminInviteCodes();
      setCodes(codesResponse.codes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormValues({
      code: '',
      ownerUserId: '',
      maxUses: '',
      benefitType: 'none',
      benefitPlanCode: '',
      durationMonths: '3',
    });
  };

  const handleOpenChange = (open: boolean) => {
    setCreateOpen(open);
    if (open) {
      resetForm();
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formValues.code.trim()) {
      setError('Code is required.');
      return;
    }

    const maxUsesValue = formValues.maxUses.trim();
    let maxUses: number | null = null;
    if (maxUsesValue) {
      const parsedMaxUses = Number(maxUsesValue);
      if (!Number.isInteger(parsedMaxUses) || parsedMaxUses <= 0) {
        setError('Max uses must be a positive number.');
        return;
      }
      maxUses = parsedMaxUses;
    }

    const benefitType = formValues.benefitType;
    const benefitPlanCode = formValues.benefitPlanCode.trim();
    let benefit: {
      type: 'subscription_months' | 'one_time_plan';
      plan_code: string;
      duration_months?: number | null;
    } | null = null;

    if (benefitType !== 'none') {
      if (!benefitPlanCode) {
        setError('Benefit plan code is required.');
        return;
      }

      if (benefitType === 'subscription_months') {
        const duration = Number(formValues.durationMonths);
        if (!Number.isInteger(duration) || duration < 1 || duration > 6) {
          setError('Subscription gift duration must be 1-6 months.');
          return;
        }
        benefit = {
          type: benefitType,
          plan_code: benefitPlanCode,
          duration_months: duration,
        };
      } else {
        benefit = {
          type: benefitType,
          plan_code: benefitPlanCode,
        };
      }
    }

    setCreating(true);
    try {
      await createAdminInviteCode({
        code: formValues.code.trim(),
        owner_user_id: formValues.ownerUserId.trim() || null,
        max_uses: maxUses,
        benefit,
      });
      setCreateOpen(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const handleDisable = async (id: string) => {
    setDisablingId(id);
    try {
      await disableAdminInviteCode(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable code');
    } finally {
      setDisablingId(null);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-auto py-4 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Invite Codes</h1>
            <p className="text-sm text-muted-foreground">
              Manage invite codes and track invitations.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadData}>
              Refresh
            </Button>
            <Button size="sm" onClick={() => handleOpenChange(true)}>
              New code
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="px-4 lg:px-6">
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle className="text-base">Action needed</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      ) : null}

      <div className="px-4 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Active invite codes</CardTitle>
            <CardDescription>
              Track status and usage for issued codes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Benefit</TableHead>
                  <TableHead>Uses</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map(code => {
                  const maxUses = code.max_uses ?? null;
                  const usesLabel = maxUses
                    ? `${code.used_count}/${maxUses}`
                    : `${code.used_count}/∞`;
                  return (
                    <TableRow key={code.id}>
                      <TableCell className="font-medium">{code.code}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(code.status)}>
                          {code.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {benefitLabel(code)}
                      </TableCell>
                      <TableCell>{usesLabel}</TableCell>
                      <TableCell>{formatDateTime(code.created_at)}</TableCell>
                      <TableCell className="text-right">
                        {code.status === 'active' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDisable(code.id)}
                            disabled={disablingId === code.id}
                          >
                            {disablingId === code.id ? 'Disabling…' : 'Disable'}
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" disabled>
                            Disabled
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loading && codes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm">
                      No invite codes created yet.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={createOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New invite code</DialogTitle>
            <DialogDescription>
              Create a new invite code for onboarding users.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={formValues.code}
                onChange={event =>
                  setFormValues(prev => ({ ...prev, code: event.target.value }))
                }
                placeholder="INVITE-2025"
                required
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="max-uses">Max uses</Label>
                <Input
                  id="max-uses"
                  type="number"
                  min={1}
                  step={1}
                  value={formValues.maxUses}
                  onChange={event =>
                    setFormValues(prev => ({
                      ...prev,
                      maxUses: event.target.value,
                    }))
                  }
                  placeholder="25"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner-user-id">Owner user ID (optional)</Label>
                <Input
                  id="owner-user-id"
                  value={formValues.ownerUserId}
                  onChange={event =>
                    setFormValues(prev => ({
                      ...prev,
                      ownerUserId: event.target.value,
                    }))
                  }
                  placeholder="User UUID"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="benefit-type">Benefit</Label>
                <Select
                  value={formValues.benefitType}
                  onValueChange={value => {
                    if (!isBenefitType(value)) return;
                    setFormValues(prev => ({
                      ...prev,
                      benefitType: value,
                      benefitPlanCode:
                        value === 'none' ? '' : prev.benefitPlanCode,
                    }));
                  }}
                >
                  <SelectTrigger id="benefit-type">
                    <SelectValue placeholder="No benefit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No benefit</SelectItem>
                    <SelectItem value="subscription_months">
                      Subscription gift
                    </SelectItem>
                    <SelectItem value="one_time_plan">One-time plan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="benefit-plan-code">Benefit plan code</Label>
                <Input
                  id="benefit-plan-code"
                  value={formValues.benefitPlanCode}
                  onChange={event =>
                    setFormValues(prev => ({
                      ...prev,
                      benefitPlanCode: event.target.value,
                    }))
                  }
                  placeholder={
                    formValues.benefitType === 'subscription_months'
                      ? 'pro_v2'
                      : 'addon_4m or signup_bonus'
                  }
                  disabled={formValues.benefitType === 'none'}
                />
              </div>
            </div>
            {formValues.benefitType === 'subscription_months' ? (
              <div className="space-y-2">
                <Label htmlFor="duration-months">Gift duration</Label>
                <Input
                  id="duration-months"
                  type="number"
                  min={1}
                  max={6}
                  step={1}
                  value={formValues.durationMonths}
                  onChange={event =>
                    setFormValues(prev => ({
                      ...prev,
                      durationMonths: event.target.value,
                    }))
                  }
                  placeholder="3"
                />
              </div>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create code'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
